import { bool, number, string, oneOf, func, arrayOf, oneOfType, object } from 'prop-types'
import {
  FormControl,
  InputAdornment,
  InputLabel,
  ListSubheader,
  OutlinedInput,
  Select as MatSelect,
  Skeleton,
} from '@mui/material'
import Option from './Option'
import { useAppTranslation } from 'i18n/hooks'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import Text, { TextTypes } from 'Components/Ui-Kits/Text'
import { Colors } from 'constants/colors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const SKELETON_ROWS = [...Array(16).keys()]

const Select = forwardRef(
  (
    {
      fullWidth = true,
      options,
      id,
      label,
      error,
      displayNone = true,
      defaultValue,
      required,
      helperText,
      hasSearch,
      onSearchChange,
      paginate,
      size,
      loading = false,
      ...rest
    },
    ref
  ) => {
    const { t } = useAppTranslation()
    const innerRef = useRef(null)
    const rootRef = useRef(null)

    useImperativeHandle(ref, () => innerRef.current)

    const labelText = useMemo(() => {
      return required ? `${label} *` : label
    }, [label, required])
    const [searchText, setSearchText] = useState('')
    const [page, setPage] = useState(1)

    // With onSearchChange the caller fetches the matching options, so filtering
    // them again here would drop hits the caller found.
    const filteredOptions = useMemo(
      () =>
        [...options]
          .filter(
            ({ title }) =>
              onSearchChange || !searchText || title.toLocaleLowerCase().includes(searchText.toLocaleLowerCase().trim())
          )
          .slice(0, paginate ? page * 20 : undefined),
      [options, searchText, paginate, page, onSearchChange]
    )

    const handleScrall = event => {
      if (!paginate || page * 20 >= options.length) return
      const leftScroll = event.target.scrollHeight - event.target.scrollTop - event.target.offsetHeight
      if (leftScroll <= 50) {
        setPage(page => page + 1)
      }
    }

    const renderValue = useCallback(
      value =>
        Array.isArray(value)
          ? value
              .map(
                value =>
                  options.find(opt => (/\d+/.test(value) ? opt.value == value : opt.value === value))?.title || ''
              )
              .join(', ')
          : options.find(opt => (/\d+/.test(value) ? opt.value == value : opt.value === value))?.title || '',
      [options]
    )

    return (
      <FormControl fullWidth error={error} size={size} ref={rootRef}>
        <InputLabel id={id}>{labelText}</InputLabel>
        <MatSelect
          label={labelText}
          labelId={id}
          className={'background_white'}
          fullWidth={fullWidth}
          MenuProps={{
            style: { zIndex: 10001, maxHeight: 400 },
            autoFocus: false,
            PaperProps: {
              onScroll: handleScrall,
              // Without a width the menu grows to fit its widest option, so a
              // single long one stretches it far past the field it belongs to.
              // Options wrap instead (see Option).
              style: { width: rootRef.current?.offsetWidth || undefined },
            },
          }}
          defaultValue={defaultValue ?? ''}
          inputRef={innerRef}
          renderValue={renderValue}
          {...rest}
          value={rest.value === null ? '' : rest.value}
        >
          {hasSearch && (
            <ListSubheader>
              <OutlinedInput
                size="small"
                autoFocus
                startAdornment={
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={['far', 'magnifying-glass']} />
                  </InputAdornment>
                }
                label={<></>}
                fullWidth
                placeholder="Search..."
                onChange={e => {
                  setSearchText(e.target.value)
                  setPage(1)
                  onSearchChange?.(e.target.value)
                }}
                onKeyDown={e => {
                  if (e.key !== 'Escape') {
                    // Prevents autoselecting item while typing (default Select behaviour)
                    e.stopPropagation()
                  }
                }}
              />
            </ListSubheader>
          )}
          {loading && (
            // Fills the menu to its max height so it does not jump when the options arrive.
            <ListSubheader disableSticky>
              {SKELETON_ROWS.map(row => (
                <Skeleton key={row} height={36} />
              ))}
            </ListSubheader>
          )}
          {displayNone && !searchText && <Option value={null}>{t('none')}</Option>}
          {filteredOptions.map(item => (
            <Option key={`${item.title}-${item.value}`} value={item.value}>
              {item.title}
            </Option>
          ))}
        </MatSelect>
        {!!helperText && (
          <Text type={TextTypes.BODY_13} color={error ? Colors.CRIMSON : Colors.BLACK}>
            {helperText}
          </Text>
        )}
      </FormControl>
    )
  }
)

export default Select
Select.displayName = 'Select'
Select.propTypes = {
  disabled: bool,
  id: string,
  label: string,
  value: oneOfType([string, number]),
  defaultValue: oneOfType([string, number]),
  fullWidth: bool,
  options: arrayOf(object),
  variant: oneOf(['filled', 'outlined', 'standard']),
  required: bool,
  onChange: func,
  onClose: func,
  onOpen: func,
  error: bool,
  displayNone: bool,
  helperText: string,
  hasSearch: bool,
  onSearchChange: func,
  paginate: bool,
  size: string,
  loading: bool,
}
